import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { sessionKey } from '@/app/query-client'
import { http } from '@/lib/api/client'
import type {
  AuditLogEntry,
  BillingInterval,
  BillingSubscription,
  BillingSummary,
  CursorPage,
  Entitlements,
  Invitation,
  LifetimeLicense,
  LifetimePlanKey,
  Member,
  PlanChangePreview,
  PlanKey,
  Resource,
  Workspace,
  WorkspaceRole,
} from '@/lib/api/types'

const base = (workspaceId: string) => `/workspaces/${workspaceId}`

export const workspaceKeys = {
  members: (id: string) => ['workspaces', id, 'members'] as const,
  invitations: (id: string) => ['workspaces', id, 'invitations'] as const,
  entitlements: (id: string) => ['workspaces', id, 'entitlements'] as const,
  billing: (id: string) => ['workspaces', id, 'billing'] as const,
  auditLog: (id: string, action?: string) => ['workspaces', id, 'audit-log', action ?? 'all'] as const,
}

export function useMembers(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: workspaceKeys.members(workspaceId),
    queryFn: async () => (await http.get<Resource<Member[]>>(`${base(workspaceId)}/members`)).data,
    enabled,
  })
}

export function useInvitations(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: workspaceKeys.invitations(workspaceId),
    queryFn: async () => (await http.get<Resource<Invitation[]>>(`${base(workspaceId)}/invitations`)).data,
    enabled,
  })
}

export function useEntitlements(workspaceId: string) {
  return useQuery({
    queryKey: workspaceKeys.entitlements(workspaceId),
    queryFn: async () => (await http.get<Resource<Entitlements>>(`${base(workspaceId)}/entitlements`)).data,
  })
}

export function useAuditLog(workspaceId: string, enabled = true, perPage = 25) {
  return useInfiniteQuery({
    queryKey: workspaceKeys.auditLog(workspaceId),
    queryFn: ({ pageParam }) =>
      http.get<CursorPage<AuditLogEntry>>(`${base(workspaceId)}/audit-logs`, {
        cursor: pageParam,
        per_page: perPage,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined,
    enabled,
  })
}

/** Invalidate everything a membership change can affect. */
function useInvalidateTeam(workspaceId: string) {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: workspaceKeys.entitlements(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'audit-log'] }),
    ])
}

export function useInviteMember(workspaceId: string) {
  const invalidate = useInvalidateTeam(workspaceId)
  return useMutation({
    mutationFn: (input: { email: string; role: WorkspaceRole; client_id?: string; project_ids?: string[] }) =>
      http.post<Resource<Invitation>>(`${base(workspaceId)}/invitations`, input),
    onSuccess: invalidate,
  })
}

export function useResendInvitation(workspaceId: string) {
  const invalidate = useInvalidateTeam(workspaceId)
  return useMutation({
    mutationFn: (invitationId: string) =>
      http.post(`${base(workspaceId)}/invitations/${invitationId}/resend`),
    onSuccess: invalidate,
  })
}

export function useRevokeInvitation(workspaceId: string) {
  const invalidate = useInvalidateTeam(workspaceId)
  return useMutation({
    mutationFn: (invitationId: string) => http.delete(`${base(workspaceId)}/invitations/${invitationId}`),
    onSuccess: invalidate,
  })
}

export function useUpdateMemberRole(workspaceId: string) {
  const invalidate = useInvalidateTeam(workspaceId)
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: WorkspaceRole }) =>
      http.patch<Resource<Member>>(`${base(workspaceId)}/members/${memberId}`, { role }),
    onSuccess: invalidate,
  })
}

export function useRemoveMember(workspaceId: string) {
  const invalidate = useInvalidateTeam(workspaceId)
  return useMutation({
    mutationFn: (memberId: string) => http.delete(`${base(workspaceId)}/members/${memberId}`),
    onSuccess: invalidate,
  })
}

/** Owners: email a member a link (to their current address) to change their sign-in email. */
export function useSendEmailChange(workspaceId: string) {
  return useMutation({
    mutationFn: (memberId: string) =>
      http.post<{ message: string }>(`${base(workspaceId)}/members/${memberId}/email-change`),
  })
}

export function useUpdateWorkspace(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name?: string; client_work_enabled?: boolean }) =>
      http.patch<Resource<Workspace>>(base(workspaceId), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionKey }),
  })
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string }) =>
      (await http.post<Resource<Workspace>>('/workspaces', input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionKey }),
  })
}

export function useSwitchWorkspace() {
  return useMutation({
    mutationFn: (workspaceId: string) => http.post(`${base(workspaceId)}/switch`),
  })
}

export function useLeaveWorkspace(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => http.post(`${base(workspaceId)}/leave`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionKey }),
  })
}

export function useTransferOwnership(workspaceId: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateTeam(workspaceId)
  return useMutation({
    mutationFn: (input: { member_id: string; password: string; acknowledge_billing?: boolean }) =>
      http.post(`${base(workspaceId)}/transfer-ownership`, input),
    onSuccess: () => Promise.all([invalidate(), queryClient.invalidateQueries({ queryKey: sessionKey })]),
  })
}

/** Owners and admins: subscription, add-ons, invoices and the plans on offer. */
export function useBilling(workspaceId: string, options: { enabled?: boolean; poll?: boolean } = {}) {
  return useQuery({
    queryKey: workspaceKeys.billing(workspaceId),
    queryFn: async () => (await http.get<Resource<BillingSummary>>(`${base(workspaceId)}/billing`)).data,
    enabled: options.enabled ?? true,
    // After a checkout, the plan changes when Lemon Squeezy's webhook arrives.
    refetchInterval: options.poll ? 3000 : false,
  })
}

/** A plan or billing change also changes allowances and the workspace's plan badge. */
function useInvalidateBilling(workspaceId: string) {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: workspaceKeys.billing(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: workspaceKeys.entitlements(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: sessionKey }),
    ])
}

/**
 * Returns Lemon Squeezy's hosted checkout URL to send the browser to. With use_balance, the
 * owner's referral balance pays part of it (a one-time discount); a referred owner's first
 * plan gets the referral discount on its own.
 */
export function useCheckout(workspaceId: string) {
  return useMutation({
    mutationFn: async (input: { plan: PlanKey; interval: BillingInterval; use_balance?: boolean }) =>
      (await http.post<Resource<{ url: string }>>(`${base(workspaceId)}/billing/checkout`, input)).data.url,
  })
}

export function useChangePlan(workspaceId: string) {
  const invalidate = useInvalidateBilling(workspaceId)
  return useMutation({
    mutationFn: (input: { plan: PlanKey; interval: BillingInterval }) =>
      http.post<Resource<BillingSubscription>>(`${base(workspaceId)}/billing/plan`, input),
    onSuccess: invalidate,
  })
}

export function usePlanPreview(
  workspaceId: string,
  change: { plan: PlanKey; interval: BillingInterval } | null,
) {
  return useQuery({
    queryKey: [...workspaceKeys.billing(workspaceId), 'preview', change?.plan, change?.interval],
    queryFn: async () =>
      (
        await http.get<Resource<PlanChangePreview>>(
          `${base(workspaceId)}/billing/plan/preview?plan=${change?.plan ?? ''}&interval=${change?.interval ?? ''}`,
        )
      ).data,
    enabled: change !== null,
  })
}

/** Drops a downgrade waiting for the renewal: the current plan carries on. */
export function useKeepPlan(workspaceId: string) {
  const invalidate = useInvalidateBilling(workspaceId)
  return useMutation({
    mutationFn: () =>
      http.delete<Resource<BillingSubscription>>(`${base(workspaceId)}/billing/plan/scheduled`),
    onSuccess: invalidate,
  })
}

export function useCancelPlan(workspaceId: string) {
  const invalidate = useInvalidateBilling(workspaceId)
  return useMutation({
    mutationFn: () => http.post<Resource<BillingSubscription>>(`${base(workspaceId)}/billing/cancel`),
    onSuccess: invalidate,
  })
}

export function useResumePlan(workspaceId: string) {
  const invalidate = useInvalidateBilling(workspaceId)
  return useMutation({
    mutationFn: () => http.post<Resource<BillingSubscription>>(`${base(workspaceId)}/billing/resume`),
    onSuccess: invalidate,
  })
}

/** Sets an add-on's units; returns a checkout URL when it's a new add-on. */
export function useSetAddon(workspaceId: string) {
  const invalidate = useInvalidateBilling(workspaceId)
  return useMutation({
    mutationFn: async ({ code, quantity }: { code: string; quantity: number }) => {
      return (
        await http.put<Resource<{ checkout_url: string | null }>>(
          `${base(workspaceId)}/billing/addons/${code}`,
          {
            quantity,
          },
        )
      ).data.checkout_url
    },
    onSuccess: invalidate,
  })
}

/** A checkout for top-ups (one-time packs); the browser goes to the returned address. */
export function useBuyPack(workspaceId: string) {
  return useMutation({
    mutationFn: async ({ code, quantity }: { code: string; quantity: number }) =>
      (await http.post<Resource<{ url: string }>>(`${base(workspaceId)}/billing/packs/${code}`, { quantity }))
        .data.url,
  })
}

/**
 * A checkout for a lifetime plan (paid once); the browser goes to the returned address. With
 * `cancelSubscription`, the subscription is cancelled at the end of its paid period only once the
 * lifetime payment goes through (never before, so an abandoned checkout changes nothing).
 */
export function useLifetimeCheckout(workspaceId: string) {
  return useMutation({
    mutationFn: async ({
      plan,
      cancelSubscription = false,
    }: {
      plan: LifetimePlanKey
      cancelSubscription?: boolean
    }) =>
      (
        await http.post<Resource<{ url: string }>>(
          `${base(workspaceId)}/billing/lifetime/${plan}`,
          cancelSubscription ? { cancel_subscription: true } : undefined,
        )
      ).data.url,
  })
}

/** A checkout for an upgrade to a bigger lifetime plan, paid as the difference. */
export function useLifetimeUpgrade(workspaceId: string) {
  return useMutation({
    mutationFn: async (plan: LifetimePlanKey) =>
      (await http.post<Resource<{ url: string }>>(`${base(workspaceId)}/billing/lifetime/${plan}/upgrade`))
        .data.url,
  })
}

/** "Withdraw from this purchase and get a full refund", within 14 days, after the confirm step. */
export function useLifetimeWithdraw(workspaceId: string) {
  const invalidate = useInvalidateBilling(workspaceId)
  return useMutation({
    mutationFn: async (license: string) =>
      (
        await http.post<Resource<LifetimeLicense>>(`${base(workspaceId)}/billing/lifetime/withdraw`, {
          license,
          confirm: true,
        })
      ).data,
    onSuccess: invalidate,
  })
}

/** A fresh, short-lived link to Lemon Squeezy's customer portal. */
export function usePortal(workspaceId: string) {
  return useMutation({
    mutationFn: async (input: { payment_method?: boolean } = {}) =>
      (await http.post<Resource<{ url: string }>>(`${base(workspaceId)}/billing/portal`, input)).data.url,
  })
}
